// src/services/azureCostEstimateContractsService.js
// Azure Table Storage Service fuer Vertraege/Nachtraege der Kostenkontrolle (KOST-009).
// Table costEstimateContracts:
//   PartitionKey = estimateId
//   RowKey       = `${code}__${uuid}`
//   Properties   = Phase, Code, Bezeichnung, Betrag, Datum, IstNachtrag
//                  + UnternehmerId, UnternehmerName, VertragNr (US-KOST-022):
//                    Unternehmer = CRM-Firma (projectPartners.RowKey) mit
//                    Namens-Snapshot; VertragNr = Vertragsnummer fuer die
//                    Zahlungsanweisung.
//                  + VertragId (US-KOST-023): bei Nachtraegen die RowKey des
//                    Vertrags, zu dem der Nachtrag gehoert (Vertragsgruppe,
//                    siehe services/costs/contractGroups.js). Bei Vertraegen leer.

import { ensureTableExists } from "./azureTableUtils.js";

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "costEstimateContracts",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const baseUrl = () =>
  `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}`;

function toBool(val) {
  return val === true || val === "true";
}

async function queryEntities(filter) {
  let all = [];
  let nextPartitionKey = null;
  let nextRowKey = null;

  do {
    let url = `${baseUrl()}?${AZURE_CONFIG.sasToken}&${filter}`;
    if (nextPartitionKey) url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
    if (nextRowKey) url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });
    if (!response.ok) {
      if (response.status === 404) break;
      throw new Error(`Azure query error: ${response.status}`);
    }
    const data = await response.json();
    all = all.concat(data.value || []);
    nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
    nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
  } while (nextPartitionKey || nextRowKey);

  return all;
}

function mapEntity(e) {
  return {
    id: e.RowKey,
    code: e.Code || "",
    phase: e.Phase || "",
    bezeichnung: e.Bezeichnung || "",
    betrag: parseFloat(e.Betrag) || 0,
    datum: e.Datum || "",
    istNachtrag: toBool(e.IstNachtrag),
    unternehmerId: e.UnternehmerId || "",
    unternehmerName: e.UnternehmerName || "",
    vertragNr: e.VertragNr || "",
    vertragId: e.VertragId || "",
  };
}

// Gemeinsamer Entity-Aufbau fuer Anlegen und Aktualisieren. PUT ersetzt die
// Entity vollstaendig — jedes Feld muss hier stehen, sonst geht es beim
// naechsten Update verloren (siehe lessons-learned 2026-08-24).
function buildEntity(estimateId, rowKey, data) {
  return {
    PartitionKey: estimateId,
    RowKey: rowKey,
    Phase: data.phase || "",
    Code: data.code || "",
    Bezeichnung: data.bezeichnung || "",
    Betrag: parseFloat(data.betrag) || 0,
    Datum: data.datum || "",
    IstNachtrag: !!data.istNachtrag,
    UnternehmerId: data.unternehmerId || "",
    UnternehmerName: data.unternehmerName || "",
    VertragNr: data.vertragNr != null ? String(data.vertragNr) : "",
    VertragId: data.vertragId || "",
    UpdatedAt: new Date().toISOString(),
  };
}

async function upsert(estimateId, rowKey, entity) {
  const pk = encodeURIComponent(estimateId);
  const rk = encodeURIComponent(rowKey);
  const url = `${baseUrl()}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Azure upsert error: ${response.status} - ${errorText}`);
  }
}

// Alle Vertraege eines Dossiers fuer eine Phase laden.
export const getContractsByEstimate = async (estimateId, phase) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const filter = phase
      ? `$filter=PartitionKey eq '${estimateId}' and Phase eq '${phase}'`
      : `$filter=PartitionKey eq '${estimateId}'`;
    const entities = await queryEntities(filter);
    return entities.map(mapEntity);
  } catch (error) {
    console.error("Load contracts error:", error);
    return [];
  }
};

// Neuen Vertrag/Nachtrag anlegen.
export const saveContract = async (estimateId, data) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const rowKey = `${data.code}__${crypto.randomUUID()}`;
    const entity = buildEntity(estimateId, rowKey, data);
    await upsert(estimateId, rowKey, entity);
    return mapEntity(entity);
  } catch (error) {
    console.error("Save contract error:", error);
    throw error;
  }
};

// Bestehenden Vertrag aktualisieren.
export const updateContract = async (estimateId, contractId, data) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const entity = buildEntity(estimateId, contractId, data);
    await upsert(estimateId, contractId, entity);
    return mapEntity(entity);
  } catch (error) {
    console.error("Update contract error:", error);
    throw error;
  }
};

// Phase umbenennen (US-KOST-019): alle Vertraege der alten Phase auf den neuen
// Phasennamen umschreiben. Der RowKey (`${code}__${uuid}`) enthaelt die Phase
// nicht — es genuegt, die Phase-Property zu ersetzen (kein copy-then-delete noetig).
export const renameContractsPhase = async (estimateId, oldPhase, newPhase) => {
  await ensureTableExists(AZURE_CONFIG.tableName);
  const filter = `$filter=PartitionKey eq '${estimateId}' and Phase eq '${String(oldPhase).replace(/'/g, "''")}'`;
  const entities = await queryEntities(filter);
  for (const e of entities) {
    const clone = { ...e, Phase: newPhase, UpdatedAt: new Date().toISOString() };
    delete clone["odata.etag"];
    delete clone.Timestamp;
    await upsert(estimateId, e.RowKey, clone);
  }
  return entities.length;
};

// Vertrag loeschen.
export const deleteContract = async (estimateId, contractId) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const pk = encodeURIComponent(estimateId);
    const rk = encodeURIComponent(contractId);
    const url = `${baseUrl()}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;
    const response = await fetch(url, { method: "DELETE", headers: { "If-Match": "*" } });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Azure delete error: ${response.status}`);
    }
  } catch (error) {
    console.error("Delete contract error:", error);
    throw error;
  }
};

// Phase kopieren (US-KOST-019): alle Vertraege der Quell-Phase in die Ziel-Phase
// duplizieren ("Kostenkontrolle als Arbeitskopie"). Jede Kopie erhaelt zwingend
// einen NEUEN RowKey (`${code}__${uuid}`) — der RowKey ist eine reine ID; mit dem
// alten wuerde die Quelle ueberschrieben statt dupliziert.
export const copyContractsPhase = async (estimateId, sourcePhase, targetPhase) => {
  await ensureTableExists(AZURE_CONFIG.tableName);
  const filter = `$filter=PartitionKey eq '${estimateId}' and Phase eq '${String(sourcePhase).replace(/'/g, "''")}'`;
  const entities = await queryEntities(filter);
  for (const e of entities) {
    const rowKey = `${e.Code || ""}__${crypto.randomUUID()}`;
    const clone = { ...e, PartitionKey: estimateId, RowKey: rowKey, Phase: targetPhase, UpdatedAt: new Date().toISOString() };
    delete clone["odata.etag"];
    delete clone.Timestamp;
    await upsert(estimateId, rowKey, clone);
  }
  return entities.length;
};

// Phase loeschen (US-KOST-019): alle Vertraege der Phase endgueltig entfernen.
export const deleteContractsPhase = async (estimateId, phase) => {
  await ensureTableExists(AZURE_CONFIG.tableName);
  const filter = `$filter=PartitionKey eq '${estimateId}' and Phase eq '${String(phase).replace(/'/g, "''")}'`;
  const entities = await queryEntities(filter);
  for (const e of entities) {
    await deleteContract(estimateId, e.RowKey);
  }
  return entities.length;
};
